import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type { Dictionary, Lang } from "@/content/translations";
import { T } from "@/content/translations";

const STORAGE_KEY = "nt_lang";

/**
 * Resolve which language to start in. Priority order is deliberate:
 *
 *   1. `?lang=` query param on the URL — lets a shareable link force a
 *      language regardless of the recipient's local preference. This is
 *      what makes `https://nabiltawfik.com/?lang=en` an English entry
 *      point we can publicise to non-Arabic readers.
 *   2. localStorage from a prior visit — repeat visitors keep the
 *      language they were last using.
 *   3. Arabic — the site's canonical mother tongue. First-time visitors
 *      with no URL hint always land here.
 */
function resolveInitialLang(): Lang {
  if (typeof window === "undefined") return "ar";
  const urlParam = new URLSearchParams(window.location.search).get("lang");
  if (urlParam === "ar" || urlParam === "en") return urlParam;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "ar" || stored === "en") return stored;
  return "ar";
}

export interface LanguageBinding {
  lang: Lang;
  isRTL: boolean;
  t: Dictionary;
  setLang: (next: Lang) => void;
  toggle: () => void;
}

export function useLanguage(): LanguageBinding {
  const [lang, setLangState] = useState<Lang>(resolveInitialLang);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute(
      "dir",
      lang === "ar" ? "rtl" : "ltr",
    );

    // Keep the URL in sync with the active language so a copy-paste of
    // any deep link carries the language with it. Arabic stays as a
    // clean URL (no query param) since it's the canonical default;
    // English appends `?lang=en`. Re-runs on path change too — when
    // the user navigates between routes, react-router replaces the URL
    // and any prior query is dropped, so we re-add `?lang=en` on each
    // navigation if English is active.
    const url = new URL(window.location.href);
    const currentParam = url.searchParams.get("lang");
    if (lang === "en" && currentParam !== "en") {
      url.searchParams.set("lang", "en");
      window.history.replaceState(null, "", url);
    } else if (lang === "ar" && currentParam === "en") {
      url.searchParams.delete("lang");
      window.history.replaceState(null, "", url);
    }
  }, [lang, location.pathname]);

  const setLang = (next: Lang) => setLangState(next);
  const toggle = () => setLangState((prev) => (prev === "ar" ? "en" : "ar"));

  return useMemo(
    () => ({
      lang,
      isRTL: lang === "ar",
      t: T[lang],
      setLang,
      toggle,
    }),
    [lang],
  );
}
